<template>
  <div>
    <div class="margin-bottom-m">
      <ProductThumbnail v-if="productData" :product="parsedProductData" :lazy-load="false" alignment="horizontal" />
    </div>

    <div class="margin-bottom-m">
      <p ref="ratingHeader" class="margin-bottom-xs large">Overall Rating*</p>
      <Reviewlet v-if="showReviewlet" :interactive="true" size="large" :show-rating="false" :rating="starRating" @onchange="setRating" />
      <Notification v-if="showRatingError" class="margin-top-xs" severity="error" :closable="false"> Please enter an overall rating. </Notification>
    </div>
    <div>
      <Form v-slot="{ validate }">
        <div class="margin-bottom-m">
          <div class="grid-x">
            <p ref="formHeader" class="large cell small-6">Write review</p>
            <p class="small-6 cell large-auto grid-x align-right">
              <button class="link-body guidelines" :data-testid="writeReviewOpenWritingGuidelines" @click.prevent="openWritingGuidelines">
                Writing guidelines
              </button>
            </p>
          </div>

          <div class="grid-x grid-margin-x">
            <div :class="`cell large-${isSignedIn ? 12 : 6}`">
              <InputText v-model="basicData.nickname" name="nickname" label="Nickname*" rule="reviewNickname" />
            </div>
            <div v-if="!isSignedIn" class="cell large-6">
              <InputText v-model="basicData.email" name="email" label="Email address*" rule="email" />
            </div>
            <div class="cell large-12">
              <InputText v-model="basicData.reviewTitle" name="reviewTitle" label="Review title*" rule="reviewTitle" />
            </div>
            <div class="cell large-12">
              <TextArea v-model="basicData.reviewText" name="reviewText" label="Review*" rule="reviewText" />
            </div>
          </div>
        </div>

        <div class="margin-bottom-m">
          <div class="grid-x margin-bottom-xs">
            <p class="large cell small-6">Add photo</p>
            <p class="small-6 cell large-auto grid-x align-right">
              <button class="link-body guidelines" :data-testid="writeReviewOpenPhotoGuidelines" @click.prevent="openPhotoGuidelines">
                Photo guidelines
              </button>
            </p>
          </div>
          <PhotoUploadComponent @update-images="handleUpdateImages" />
        </div>

        <div v-if="reviewVideoUploadEnabled" class="margin-bottom-m">
          <div class="grid-x margin-bottom-xs">
            <p class="large cell small-6">Add a video</p>
            <p class="small-6 cell large-auto grid-x align-right">
              <button class="link-body guidelines" data-testid="writeReviewOpenVideoGuidelines" @click.prevent="openVideoGuidelines">
                Video guidelines
              </button>
            </p>
          </div>
          <VideoUploadComponent @update-video="handleUpdateVideo" @is-loading="handleVideoIsLoading" />
        </div>

        <div class="would-recommend">
          <div :id="WRITE_REVIEW_UI_IDS.doRecommend" class="large large-margin-right-s medium-margin-right-s small-margin-bottom-xs" tabindex="0">
            Would you recommend this product to a friend?
          </div>
          <Radio v-model="basicData.doRecommend.value" :options="basicData.doRecommend.options" spacing="margin-right-xs" name="doRecommend" />
        </div>

        <div v-if="isMcom">
          <p>
            By clicking Submit you acknowledge that you are at least 18 years old and agree to Macy's
            <a
              class="link-body"
              href="https://www.macys.com/customer-service/articles/user-generated-content-policy"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="User Generated Content Policy, opens in a new window"
            >
              User Generated Content Policy</a
            >.
          </p>
        </div>

        <div v-else class="small">
          <template v-if="renderOnClient">
            <ExtraQuestion
              v-for="extraQuestion in extraQuestions"
              :key="extraQuestion.name"
              :name="extraQuestion.name"
              :title="extraQuestion.title"
              :type="extraQuestion.type"
              :options="extraQuestion.options"
              :question-value="extraQuestion.value"
              @update:question-value="updateExtraQuestionValue(extraQuestion, $event)"
            />
          </template>
          <div class="sweepstakes">
            <div :id="WRITE_REVIEW_UI_IDS.sweepstakes" class="large large-margin-right-s medium-margin-right-s small-margin-bottom-xs" tabindex="0">
              Would you like to be entered in the
              <a
                class="link-body"
                href="https://assets.bloomingdales.com/dyn_img/cat_splash/BloomingdalesProductReviewSweepstakesOfficialRules.pdf"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Review To Win Sweepstakes, opens in a new window"
              >
                Review To Win Sweepstakes</a
              >?
            </div>
            <Radio v-model="basicData.sweepstakes.value" :options="basicData.sweepstakes.options" spacing="margin-right-xs" name="sweepstakes" />
          </div>
          <div v-if="isReviewFormParityEnabled">
            <p class="bcom-ugc-policy">
              By clicking Submit you acknowledge that you are at least 18 years old and agree to Bloomingdale's
              <a
                class="link-body"
                href="https://customerservice-bloomingdales.com/articles/user-generated-content"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="User Generated Content Policy, opens in a new window"
              >
                User Generated Content Policy</a
              >.
            </p>
          </div>
          <div v-else :data-testid="writeReviewDoAgree">
            <div :class="{ 'margin-bottom-m': !basicData.doAgree.isError, 'margin-bottom-xxs': basicData.doAgree.isError }">
              <Checkbox v-model="basicData.doAgree.value" name="checkbox" :options="basicData.doAgree.options" />
            </div>
            <div v-if="basicData.doAgree.isError" class="form-error small margin-bottom-m">
              <div class="error-red-medium"></div>
              <span>Please agree to the Terms & Conditions.</span>
            </div>
            You may receive emails regarding this submission. Any emails will include the ability to opt-out of future communications.
          </div>
        </div>
        <Notification v-if="showSubmittingError" class="margin-top-m" severity="error" :closable="false">
          Something went wrong. Please try again later.
        </Notification>
        <div class="grid-x align-center margin-top-l">
          <button
            class="button emphasis small-12 medium-6"
            :data-testid="writeReviewSubmitButton"
            :disabled="isSubmittingReview || isVideoUploading"
            @click.prevent="submit(validate)"
          >
            Submit
          </button>
        </div>
      </Form>
    </div>
    <ReviewGuidelines :type="guidelineType" />
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, watch, onMounted, onBeforeMount } from 'vue';
import Reviewlet from '@atomic-ui/reviewlet/Reviewlet';
import InputText from '@atomic-ui/inputText/InputText';
import TextArea from '@atomic-ui/textArea/TextArea';
import Checkbox from '@atomic-ui/checkboxBasic/CheckboxBasic';
import Radio from '@atomic-ui/radio/Radio';
import Form from '@atomic-ui/form';
import Notification from '@atomic-ui/notification/Notification';
import { useModalOverlayStore } from '@nuxt-common/atomic-ui/app/stores/useModalOverlayStore';
import ProductThumbnail from '@nuxt-common/product-thumbnail/app/components/ProductThumbnail.vue';
import ReviewGuidelines from '~/modules/reviews/components/writeReview/ReviewGuidelines';
import PhotoUploadComponent from '~/modules/reviews/components/writeReview/PhotoUploadComponent';
import VideoUploadComponent from '~/modules/reviews/components/writeReview/VideoUploadComponent';
import useColor from '~/modules/color/composables/useColor';
import { extraQuestionsList, FIELD_TYPES, GUIDELINES_TYPES, questionsForCategory } from '~/modules/reviews/components/writeReview/constants';
import { useBrand } from '~/composables/useBrand';
import useWriteReview from '~/modules/reviews/composables/useWriteReview';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { usePageData } from '~/composables/usePageData';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import ExtraQuestion from './ExtraQuestion';
import { STRING_SHORT_ANSWERS, BOOLEAN_SHORT_ANSWERS } from '../../../../constants/common';
import { writeReviewGuidelines } from '../../../../constants/modals';
import {
  writeReviewDoAgree,
  writeReviewOpenPhotoGuidelines,
  writeReviewOpenWritingGuidelines,
  writeReviewSubmitButton,
} from '../../constants/testIds';

const WRITE_REVIEW_UI_IDS = {
  doRecommend: 'doRecommendRadioField',
  sweepstakes: 'sweepstakesRadioField',
};

const { isMcom } = useBrand();
const {
  killswitches: { isReviewFormParityEnabled = false, reviewVideoUploadEnabled = false },
} = useKillswitches();
const { isShopTheSetExpEnabled } = useFeatureEligibility();

const { productId, productData } = useProduct();
const { selectedColor } = useColor();

/// Guidelines
const modalOverlayStore = useModalOverlayStore();
const guidelineType = ref(GUIDELINES_TYPES.WRITE);
const openWritingGuidelines = () => {
  guidelineType.value = GUIDELINES_TYPES.WRITE;
  modalOverlayStore.setModalOverlayVisibility(writeReviewGuidelines);
};
const openPhotoGuidelines = () => {
  guidelineType.value = GUIDELINES_TYPES.PHOTO;
  modalOverlayStore.setModalOverlayVisibility(writeReviewGuidelines);
};
const openVideoGuidelines = () => {
  guidelineType.value = GUIDELINES_TYPES.VIDEO;
  modalOverlayStore.setModalOverlayVisibility(writeReviewGuidelines);
};
const renderOnClient = ref(false);
/// Form
const { reviewsData, user } = usePageData();
const { isSignedIn } = user;
const basicData = ref({
  nickname: '',
  email: '',
  reviewTitle: '',
  reviewText: '',
  doAgree: {
    value: [],
    isError: false,
    options: [
      {
        id: 'doAgree',
        // only loads chunk on BCOM (see template next to 'Checkbox')
        label: defineAsyncComponent(() => import(/* webpackChunkName: "TermsAndConditionsComponent" */ './TermsAndConditionsComponent')),
        labelType: 'html',
        ariaLabel: 'I agree to the Terms & Conditions',
        value: true,
      },
    ],
  },
  sweepstakes: {
    value: null,
    options: [
      {
        label: STRING_SHORT_ANSWERS.YES,
        id: 'sweepstakes-true',
        value: 'True',
        ariaDescribedBy: WRITE_REVIEW_UI_IDS.sweepstakes,
      },
      {
        label: STRING_SHORT_ANSWERS.NO,
        id: 'sweepstakes-false',
        value: 'False',
        ariaDescribedBy: WRITE_REVIEW_UI_IDS.sweepstakes,
      },
    ],
  },
  doRecommend: {
    value: null,
    options: [
      {
        label: STRING_SHORT_ANSWERS.YES,
        id: 'doRecommend-true',
        value: STRING_SHORT_ANSWERS.YES,
        ariaDescribedBy: WRITE_REVIEW_UI_IDS.doRecommend,
      },
      {
        label: STRING_SHORT_ANSWERS.NO,
        id: 'doRecommend-false',
        value: STRING_SHORT_ANSWERS.NO,
        ariaDescribedBy: WRITE_REVIEW_UI_IDS.doRecommend,
      },
    ],
  },
  photos: [],
  video: null,
});

const handleUpdateImages = (/** @type {WriteReviewObject['photos']} */ updatedImages) => {
  basicData.value.photos = updatedImages;
};

const handleUpdateVideo = (updatedVideo) => {
  basicData.value.video = updatedVideo;
};

const isVideoUploading = ref(false);
const handleVideoIsLoading = (loading) => {
  isVideoUploading.value = loading;
};

/** @typedef {Object} ExtraQuestion
 * @property {string | object | Array<string>} value
 * @property {string} name
 * @property {string} title
 * @property {string} type
 * @property {Array<ExtraQuestionOption>} options
 */

/** @type import('vue').Ref<Array<ExtraQuestion>> */
const extraQuestions = ref([]);
const showRatingError = ref(false);

const { query } = useRoute();
const starRatingFromUrl = Number(query.starRating);
const memberIdFromUrl = query.memberId ? Number(query.memberId) : null;
const memberIdSTS = isShopTheSetExpEnabled ? productId : null;
/** @type {import('vue').Ref<number?>} */
const starRating = ref(null);
const setRating = (rating) => {
  starRating.value = rating;
  showRatingError.value = false;
};

/**
 * Returns true if rating is between 0 and 5
 *
 * @param {number} rating
 * @returns {boolean}
 */

const validateStarRating = (rating) => !!(rating && rating > 0 && rating <= 5);
const showReviewlet = ref(false);
onMounted(() => {
  if (starRatingFromUrl && validateStarRating(starRatingFromUrl)) {
    setRating(starRatingFromUrl);
  }
  showReviewlet.value = true;
});

const updateExtraQuestionValue = (extraQuestion, x) => {
  extraQuestion.value = x;
};

watch(basicData.value.doAgree.value, (value) => {
  basicData.value.doAgree.isError = !value[0];
});
onMounted(() => {
  renderOnClient.value = true;
});
/** @type {import('vue').Ref<null | HTMLElement>} */
const formHeader = ref(null);
/** @type {import('vue').Ref<null | HTMLElement>} */
const ratingHeader = ref(null);

/** @param {import('vue').Ref<null | HTMLElement>} element */
function scrollTo(element) {
  element.value?.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  });
}

async function validateAll(validateForm) {
  let result = true;

  if (!isMcom) {
    if (!basicData.value.doAgree.value[0] && !isReviewFormParityEnabled) {
      basicData.value.doAgree.isError = true;
      result = false;
    }
  }

  const { valid: isFormValid } = await validateForm();
  if (!starRating.value) {
    showRatingError.value = true;
    scrollTo(ratingHeader);
    return false;
  }

  if (!isFormValid) {
    scrollTo(formHeader);
    return false;
  }

  return result;
}

const { submitReview, isSubmittingReview, showSubmittingError } = useWriteReview();
async function submit(validateForm) {
  if (!(await validateAll(validateForm))) return;
  await submitReview(
    {
      rating: starRating.value,
      reviewTitle: basicData.value.reviewTitle,
      reviewText: basicData.value.reviewText,
      nickname: basicData.value.nickname,
      email: basicData.value.email,
      doRecommend: BOOLEAN_SHORT_ANSWERS[basicData.value.doRecommend.value ?? ''],
      sweepstakes: basicData.value.sweepstakes.value || 'False',
      photos: basicData.value.photos,
      video: basicData.value.video,
      extraQuestions: extraQuestions.value.map((curr) => ({
        name: curr.name,
        value: curr.value,
        type: curr.type,
      })),
    },
    memberIdFromUrl || memberIdSTS,
  );
}

function prepareExtraQuestions(categoryName) {
  if (!categoryName) throw new TypeError('No such category');
  return questionsForCategory[categoryName].map((x) => {
    let value;
    switch (extraQuestionsList[x].type) {
      case FIELD_TYPES.scale:
        value = null;
        break;
      case FIELD_TYPES.tiles:
        value = [];
        break;
      case FIELD_TYPES.size:
        extraQuestionsList[x].options.forEach((option) => {
          value = { ...value, [option.name]: '' };
        });
        break;
      default:
        value = null;
    }
    return { ...extraQuestionsList[x], value };
  });
}

// TODO - remove 'parsedProductData' and resolve parsing issue within 'ProductThumbnail' component
// the color maps in the product data has been parsed
const parsedProductData = computed(() => ({
  ...(productData ?? {}),
  traits: {
    ...(productData?.traits ?? {}),
    colors: {
      ...(productData?.traits?.colors ?? {}),
      // include only selected color entry or nothing in colorMap arr
      colorMap: [...(productId && selectedColor.value ? [selectedColor.value] : [])],
    },
  },
}));

onBeforeMount(() => {
  basicData.value.nickname = reviewsData.lastReviewUsername || user.userName || '';
  // TODO: For first reviews categoryName is empty string. I added that case also to quick return
  if (isMcom || !productData?.detail?.reviewCategoryName) return;
  extraQuestions.value = prepareExtraQuestions(productData?.detail?.reviewCategoryName);
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@csgIcons/new/error-red-medium';

:deep(.radio-container) {
  display: flex;
  flex-wrap: wrap;
}

:deep(.radio-wrapper label) {
  cursor: pointer;
}
/**
@description
The image field in the product thumbnail was not rendering, although all the attribute fields were defined.
Suppressing the original container-type value allows the image to now show as expected on all devices and browsers

Where does it live?
file location of overridden selector:  "@common-vue/product-thumbnail/app/components/ProductThumbnail/ProductThumbnailImage.vue"
*/
:deep(.product-image div.v-product-thumbnail-image-container) {
  // prev value -> inline-size
  container-type: normal;
}

.would-recommend,
.sweepstakes {
  @include margin-bottom-m;
  @include breakpoint(medium up) {
    display: flex;
  }
}

button.primary {
  width: rem-calc(335);
}
:deep(.product-description .expand) {
  display: none;
}

.form-error {
  display: flex;
  align-items: center;
  gap: rem-calc(8);
  color: $error-color;
}
</style>

<style lang="scss" scoped brand="mcom">
.guidelines {
  text-transform: capitalize;
}
</style>
